import { Box, Link, Typography } from "@mui/material";
import { BaseTable } from "components";
import * as sort from "enums/sort";
import { UseMaterialColors, UseTheme } from "lib/hooks";
import { GradientHeaderText } from "lib/styles/header";
import { GetTableCellStyles, TableHeaderStyles } from "lib/styles/table";
import {
	PlantUserAccessManagerContact,
	PlantUserAccessManagerContactInfo,
} from "models/dto/user";
import { useCallback, useMemo, useState } from "react";

import { CreatePlantUserHeadCells } from "./accessContactInfoHeadCells";

/** Sort state for one plant-user access manager table (per sector block on the contact page). */
export type PlantUserAccessManagersTableSortState = {
	order: "asc" | "desc";
	orderBy: keyof PlantUserAccessManagerContact;
};

export type PlantUserAccessManagersTableProps = {
	sectorData: PlantUserAccessManagerContactInfo;
	loading: boolean;
	materialColors: ReturnType<typeof UseMaterialColors>;
	theme: ReturnType<typeof UseTheme>["theme"];
};

/** Renders managers for a single sector (name, plants, email). */
export function PlantUserAccessManagersTable({
	sectorData,
	loading,
	materialColors,
	theme,
}: PlantUserAccessManagersTableProps) {
	const [sortState, setSortState] = useState<PlantUserAccessManagersTableSortState>(
		{
			order: "asc",
			orderBy: "name",
		}
	);

	const handlePlantUserTableRequestSort = useCallback(
		(property: keyof PlantUserAccessManagerContact) => {
			setSortState((prev) => {
				const isAsc = prev.orderBy === property && prev.order === "asc";
				return {
					order: isAsc ? "desc" : "asc",
					orderBy: property,
				};
			});
		},
		[]
	);

	const sortedManagers = useMemo(() => {
		const { order, orderBy } = sortState;
		return [...sectorData.managers].sort((a, b) => {
			const getValue = (
				manager: PlantUserAccessManagerContact,
				key: keyof PlantUserAccessManagerContact
			): string => {
				const val = manager[key];
				if (val === null || val === undefined) {
					return "";
				}
				return String(val).toLowerCase();
			};
			const aValue = getValue(a, orderBy);
			const bValue = getValue(b, orderBy);
			const cmp = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
			return order === "asc" ? cmp : -cmp;
		});
	}, [sectorData.managers, sortState]);

	const headCells = useMemo(() => {
		const fieldMap: Record<string, keyof PlantUserAccessManagerContact> = {
			name: "name",
			plants: "plants",
			email: "email",
		};
		return CreatePlantUserHeadCells(materialColors, (property) => {
			const field = fieldMap[property];
			if (field) {
				handlePlantUserTableRequestSort(field);
			}
		});
	}, [materialColors, handlePlantUserTableRequestSort]);

	const plantUserTableSortEnum = useMemo(() => {
		const sortField = sortState.orderBy as string;
		return sortState.order === "asc"
			? sort.Asc(sortField)
			: sort.Desc(sortField);
	}, [sortState]);

	return (
		<BaseTable<
			PlantUserAccessManagerContact,
			keyof PlantUserAccessManagerContact
		>
			data={sortedManagers}
			headCells={headCells}
			order={plantUserTableSortEnum}
			config={{
				stickyHeader: true,
				header: {
					title: (
						<Typography
							variant="h5"
							sx={GradientHeaderText(theme, false)}
						>
							{sectorData.sectorName}
						</Typography>
					),
				},
			}}
			loading={loading}
			hideActions={true}
			sx={TableHeaderStyles(theme)}
			rowMapper={(manager, cell) => {
				const cellStyles = GetTableCellStyles();
				if (cell.id === "name") {
					return (
						<Box component="span" sx={cellStyles.name}>
							{manager.name}
						</Box>
					);
				}
				if (cell.id === "plants") {
					return (
						<Box
							component="span"
							title={manager.plants}
							sx={{
								...cellStyles.name,
								whiteSpace: "normal",
								wordBreak: "break-word",
							}}
						>
							{manager.plants || "N/A"}
						</Box>
					);
				}
				if (cell.id === "email") {
					return (
						<Link
							href={`mailto:${manager.email}`}
							underline="hover"
							sx={cellStyles.email}
						>
							{manager.email}
						</Link>
					);
				}
				return undefined;
			}}
			getRowId={(manager) => manager.userId}
		/>
	);
}
